
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import { assertIsFn, AddContext } from "@storybook/addon-docs";

import { Canvas, Meta, Story } from '@storybook/addon-docs';
import template from './icon.twig';
// The '!!raw-loader!' syntax is a non-standard, Webpack-specific, syntax.
// See: https://github.com/webpack-contrib/raw-loader#examples
// For now, it seems likely Storybook is pretty tied to Webpack, therefore, we are
// okay with the following Webpack-specific raw loader syntax. It's better to leave
// the ESLint rule enabled globally, and only thoughtfully disable as needed (e.g.
// within a Storybook docs page and not within an actual component).
// This can be revisited in the future if Storybook no longer relies on Webpack.
// eslint-disable-next-line @cloudfour/import/no-webpack-loader-syntax
import a11yDemoSource from '!!raw-loader!./demo/a11y.twig';
import a11yDemo from './demo/a11y.twig';
// eslint-disable-next-line @cloudfour/import/no-webpack-loader-syntax
import inlineDemoSource from '!!raw-loader!./demo/inline.twig';
import inlineDemo from './demo/inline.twig';
const iconStory = (args) => {
  // Don't bother with the inline option if it is the default
  if (args.inline === false) {
    delete args.inline;
  }
  return template(args);
};


const layoutProps = {
  
};
const MDXLayout = "wrapper"
function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Meta title="Components/Icon" mdxType="Meta" />
    <h1>{`Icon`}</h1>
    <p>{`The `}<inlineCode parentName="p">{`c-icon`}</inlineCode>{` class causes an SVG element to inherit its size and default color from its parent element's `}<inlineCode parentName="p">{`font-size`}</inlineCode>{` and `}<inlineCode parentName="p">{`color`}</inlineCode>{` value.`}</p>
    <p>{`For a list of available icons, visit `}<a parentName="p" {...{
        "href": "/docs/design-icons--page"
      }}>{`the Icons page`}</a>{`.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Basic" args={{
        name: 'heart',
        inline: false
      }} argTypes={{
        name: {
          type: {
            name: 'string'
          }
        },
        fallback: {
          type: {
            name: 'string'
          }
        },
        inline: {
          type: {
            name: 'boolean'
          }
        },
        class: {
          type: {
            name: 'string'
          }
        },
        title: {
          type: {
            name: 'string'
          }
        }
      }} mdxType="Story">
    {args => iconStory(args)}
  </Story>
    </Canvas>
    <h2>{`Accessibility`}</h2>
    <p>{`By default, an icon is assumed to be decorative. If the `}<inlineCode parentName="p">{`title`}</inlineCode>{` property is provided, it will be used as the icon's accessible fallback text. The default values for `}<inlineCode parentName="p">{`aria-hidden`}</inlineCode>{` and `}<inlineCode parentName="p">{`role`}</inlineCode>{` will be adjusted accordingly as well. (`}<a parentName="p" {...{
        "href": "https://css-tricks.com/accessible-svgs/#aa-example-1-standalone-icon-meaningful"
      }}>{`More info`}</a>{`)`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Accessibility" parameters={{
        docs: {
          source: {
            code: a11yDemoSource
          }
        }
      }} mdxType="Story">
    {a11yDemo}
  </Story>
    </Canvas>
    <h2>{`Modifier: Inline`}</h2>
    <p>{`Icons without a flex or grid container may appear `}<a parentName="p" {...{
        "href": "https://cloudfour.com/thinks/some-imaginary-css/#align-to-typeface-median"
      }}>{`slightly low in comparison to adjacent text`}</a>{`. The `}<inlineCode parentName="p">{`c-icon--inline`}</inlineCode>{` modifier applies a slight positioning adjustment to account for this.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Inline" parameters={{
        docs: {
          source: {
            code: inlineDemoSource
          }
        }
      }} mdxType="Story">
    {inlineDemo}
  </Story>
    </Canvas>
    <h2>{`CSS Properties`}</h2>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`--icon-size`}</inlineCode>{`: By default, icons are sized based on `}<inlineCode parentName="li">{`font-size`}</inlineCode>{`. Alternatively, `}<inlineCode parentName="li">{`--icon-size`}</inlineCode>{` may be used in cases where you want to size an icon without impacting `}<inlineCode parentName="li">{`font-size`}</inlineCode>{`.`}</li>
    </ul>
    <h2>{`Template Properties`}</h2>
    <p>{`The component template supports the following unique properties:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`class`}</inlineCode>{`: Will append to `}<inlineCode parentName="li">{`c-icon`}</inlineCode>{` and any other modifier classes.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`fallback`}</inlineCode>{`: The name of an icon to display if the one specified by `}<inlineCode parentName="li">{`name`}</inlineCode>{` doesn't exist. Useful if you are displaying unique icons based on dynamic content. Defaults to `}<inlineCode parentName="li">{`x`}</inlineCode>{`.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`inline`}</inlineCode>{`: When `}<inlineCode parentName="li">{`true`}</inlineCode>{`, the inline modifier will be applied.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`name`}</inlineCode>{`: The name of the intended icon `}<a parentName="li" {...{
          "href": "/docs/design-icons--page"
        }}>{`in our library`}</a>{`.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`title`}</inlineCode>{`: If provided, a `}<inlineCode parentName="li">{`title`}</inlineCode>{` element will be prepended to the icon source with the contents of this property. This will also set the `}<inlineCode parentName="li">{`role`}</inlineCode>{` to `}<inlineCode parentName="li">{`img`}</inlineCode>{` unless it is already specified. (`}<a parentName="li" {...{
          "href": "https://css-tricks.com/accessible-svgs/#aa-example-1-standalone-icon-meaningful"
        }}>{`More info`}</a>{`)`}</li>
    </ul>
    <p>{`Additional SVG properties will be passed to the Twig template for the asset itself:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-twig"
      }}>{`{% include '@cloudfour/components/icon/icon.twig' with {
  name: 'heart',
  inline: true,
  viewBox: '-12 -12 48 48',
  aria_hidden: 'true'
} %}
`}</code></pre>
    </MDXLayout>;
}

;
MDXContent.isMDXComponent = true;

export const basic = (args => iconStory(args));
basic.storyName = 'Basic';
basic.argTypes = {
  name: {
    type: {
      name: 'string'
    }
  },
  fallback: {
    type: {
      name: 'string'
    }
  },
  inline: {
    type: {
      name: 'boolean'
    }
  },
  class: {
    type: {
      name: 'string'
    }
  },
  title: {
    type: {
      name: 'string'
    }
  }
};
basic.args = {
  name: 'heart',
  inline: false
};
basic.parameters = { storySource: { source: 'args => iconStory(args)' } };

export const accessibility = assertIsFn(a11yDemo);
accessibility.storyName = 'Accessibility';
accessibility.parameters = { storySource: { source: 'a11yDemo' }, ...{
  docs: {
    source: {
      code: a11yDemoSource
    }
  }
} };

export const inline = assertIsFn(inlineDemo);
inline.storyName = 'Inline';
inline.parameters = { storySource: { source: 'inlineDemo' }, ...{
  docs: {
    source: {
      code: inlineDemoSource
    }
  }
} };

const componentMeta = { title: 'Components/Icon', includeStories: ["basic","accessibility","inline"],  };

const mdxStoryNameToKey = {"Basic":"basic","Accessibility":"accessibility","Inline":"inline"};

componentMeta.parameters = componentMeta.parameters || {};
componentMeta.parameters.docs = {
  ...(componentMeta.parameters.docs || {}),
  page: () => <AddContext mdxStoryNameToKey={mdxStoryNameToKey} mdxComponentAnnotations={componentMeta}><MDXContent /></AddContext>,
};

export default componentMeta;